import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { LAST_ADMIN_ERROR, OVER_MAX_MEMBERS, SUBSCRIPTION_EXPIRED } from '@air/errors';
import { Button } from '@air/primitive-button';

import { useAccountProvisionerContext } from '~/components/AccountProvisioner/providers/AccountProvisionerProvider';
import { BirdHoldingUmbrellaAsset } from '~/components/Assets/BirdHoldingUmbrella';
import { Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { useLogout } from '~/swr-hooks/auth/useLogout';
import { isSafeRedirectUrl } from '~/utils/auth/isSafeRedirectUrl';

const getErrorTitle = (error: string) => {
  switch (error) {
    case SUBSCRIPTION_EXPIRED.type:
      return 'Workspace subscription expired';
    case OVER_MAX_MEMBERS.type:
      return 'Workspace member limit reached';
    case LAST_ADMIN_ERROR.type:
      return 'Unable to access workspace';
    default:
      return 'Unable to complete sign-in';
  }
};

const getErrorMessage = (error: string) => {
  switch (error) {
    case SUBSCRIPTION_EXPIRED.type:
      return `Your organization's workspace subscription has expired. Please contact your administrator to resolve this issue.`;
    case OVER_MAX_MEMBERS.type:
      return `Your organization's workspace has reached its maximum member capacity. Please contact your administrator to resolve this issue.`;
    case LAST_ADMIN_ERROR.type:
      return 'Your access to a workspace was revoked, but you were the only Admin. Please contact your Air representative to appoint a new Admin.';
    default:
      return 'We encountered an issue while attempting to access your workspace. This could be due to a temporary system problem or an access-related issue.';
  }
};

export const AccountProvisioningFailedState = memo(() => {
  const { accountProvisioner, setContinuedWithAccountProvisioningError } = useAccountProvisionerContext();
  const { logout } = useLogout();
  const router = useRouter();
  const rawRedirectUrl = typeof router.query.redirect === 'string' ? router.query.redirect : undefined;
  const redirectUrl = rawRedirectUrl && isSafeRedirectUrl(rawRedirectUrl) ? rawRedirectUrl : undefined;
  const error = accountProvisioner?.data.log?.[0] || '';

  const errorTitle = getErrorTitle(error);
  const errorMessage = getErrorMessage(error);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const handleJoinOrCreateWorkspace = useCallback(() => {
    setContinuedWithAccountProvisioningError(true);
    router.push(
      redirectUrl
        ? `${Routes.joinApprovedWorkspace}?${QueryParamNames.redirect}=${encodeURIComponent(redirectUrl)}`
        : Routes.joinApprovedWorkspace,
    );
  }, [redirectUrl, router, setContinuedWithAccountProvisioningError]);

  return (
    <>
      <BirdHoldingUmbrellaAsset className="mb-5" />
      <h1 className="mb-5 max-w-xl text-center font-semibold ads:text-primary text-32 text-gray-12 md:mb-7 md:text-40">
        {errorTitle}
      </h1>
      <p className="mb-5 max-w-md text-balance text-center ads:text-metadata text-16 text-gray-10">{errorMessage}</p>
      <div className="mt-4 mb-6">
        <Button size="extra-large" color="blue" onClick={handleLogout}>
          Back to login
        </Button>
      </div>
      <button
        className="font-medium ads:text-metadata text-14 text-gray-10 underline ads:hover:text-primary hover:text-gray-12"
        onClick={handleJoinOrCreateWorkspace}
        type="button"
      >
        Join or create a different workspace
      </button>
    </>
  );
});

AccountProvisioningFailedState.displayName = 'AccountProvisioningFailedState';
